import React, { useState, useEffect } from 'react';
import { Input } from '../../atoms/Input';
import { ContactProps } from './types';

// Regular expressions for validation
const VALIDATION_PATTERNS = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  phone: /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,5}[-\s\.]?[0-9]{1,4}$/,
};

const VARIANT_CONFIG = {
  phone: {
    type: 'tel' as const,
    placeholder: '+1 (555) 000-0000',
    label: 'Phone Number',
    pattern: VALIDATION_PATTERNS.phone,
    errorMessage: 'Please enter a valid phone number',
  },
  email: {
    type: 'email' as const,
    placeholder: 'you@example.com',
    label: 'Email Address',
    pattern: VALIDATION_PATTERNS.email,
    errorMessage: 'Please enter a valid email address',
  },
};

export const Contact = React.forwardRef<HTMLInputElement, ContactProps>(
  ({ variant, label, helperText, value, onChange, onBlur, onValidate, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(value || '');
    const [error, setError] = useState<string>('');
    const [touched, setTouched] = useState(false);

    const config = VARIANT_CONFIG[variant];
    const effectiveError = props.error || error;
    const effectiveLabel = label === undefined ? config.label : label;

    useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value);
      }
    }, [value]);

    const validateValue = (val: string | number | readonly string[] | undefined): boolean => {
      const strValue = String(val || '');

      if (!strValue) {
        return true; // Empty is valid unless required
      }

      const isValid = config.pattern.test(strValue);
      return isValid;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);

      if (touched) {
        const isValid = validateValue(newValue);
        setError(isValid ? '' : config.errorMessage);
        onValidate?.(isValid);
      }

      onChange?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      const isValid = validateValue(e.target.value);
      setError(isValid ? '' : config.errorMessage);
      onValidate?.(isValid);
      onBlur?.(e);
    };

    return (
      <Input
        ref={ref}
        type={config.type}
        label={effectiveLabel || undefined}
        placeholder={props.placeholder || config.placeholder}
        value={value !== undefined ? value : internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={effectiveError}
        helperText={!effectiveError ? helperText : undefined}
        {...props}
      />
    );
  }
);

Contact.displayName = 'Contact';

export type { ContactProps, ContactVariant } from './types';
